import { FormTypes } from "../../../utils/models";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import AboutHelpModal from "./AboutHelpModal";
import { Alert, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import CustomStepper from "../../../components/CustomStepper";
import FormLayout from "../../../components/FormLayout";
import InfoCircleIcon from "@mui/icons-material/InfoRounded";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import { Link } from "react-router-dom";
import { taxSlice } from "../../../store/reducer/tax";
import { goBack, push } from "redux-first-history";
import {
  ConfirmSchedule1Task,
  DeleteSchedule1Task,
  selectTask,
  UploadSchedule1Task,
} from "../../../utils/tasks";
import { RootState } from "../../../store";
import { User } from "../../../model/user";
import { TaxDocument, TaxDocumentError } from "../../../model/tax/tax_document";
import { IRSSchedule1Form } from "../../../model/tax/irsschedule1";
import ConfirmSchedule1 from "./ConfirmSchedule1";
import FileDropContainer from "../../../components/FileInputWithDragAndDrop/FileDropContainer";
import { FileRejection } from "react-dropzone";
import DocumentList from "./DocumentList";
import { userSetupSlice } from "../../../store/reducer/user_setup";
import { ErrorAlert } from "../../../components/Alerts";
import { ExitType } from "../../../constants/common"

const steps = ["upload schedule 1", "confirm"];
interface FormsSchedule1Props {
  taxState: RootState["tax"];
  user: User | null;
}

const FormsSchedule1 = ({ user, taxState }: FormsSchedule1Props) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [invalidSchedule1Exists, setInvalidSchedule1Exists] = useState(false);

  const uploadSch1Task = selectTask(taxState, UploadSchedule1Task.id);
  const confirmSch1Task = selectTask(taxState, ConfirmSchedule1Task.id);
  const deleteSch1Task = selectTask(taxState, DeleteSchedule1Task.id);

  const lastSch1 =
    taxState.irsSchedule1Forms[taxState.irsSchedule1Forms.length - 1];

  useEffect(() => {
    dispatch(taxSlice.actions.setEditMode({}));
    dispatch(taxSlice.actions.fetchSchedule1FormDetails());
  }, [dispatch]);

  // Gracefully kick out users who upload a Schedule 1 with rent/royalities and/or other gains and losses
  // Rather than encounter the backend's validation form submit error
  useEffect(() => {
    if (typeof lastSch1 !== "undefined" && !(lastSch1 instanceof Error) && !(lastSch1.document_name == 'error')) {
      let royalties = (lastSch1 as TaxDocument<IRSSchedule1Form>).form_data
        .rentals_royalties_etc;
      let otherGains = (lastSch1 as TaxDocument<IRSSchedule1Form>).form_data
        .other_gains_or_losses;
      let royaltiesAreNonZero =
        typeof royalties !== "undefined" &&
        (Number(royalties) > 0 || Number(royalties) < 0);
      let otherGainsAreNonZero =
        typeof otherGains !== "undefined" &&
        (Number(otherGains) > 0 || Number(otherGains) < 0);
      if (royaltiesAreNonZero && otherGainsAreNonZero) {
        setInvalidSchedule1Exists(true);
        dispatch(userSetupSlice.actions.setExitMessageType(ExitType.UploadedSch1WithRoyaltiesAndOtherGains));
        dispatch(push("/upload/sorry"));
      } else if (royaltiesAreNonZero) {
        setInvalidSchedule1Exists(true);
        dispatch(userSetupSlice.actions.setExitMessageType(ExitType.UploadedSch1WithRoyalties));
        dispatch(push("/upload/sorry"));
      } else if (otherGainsAreNonZero) {
        setInvalidSchedule1Exists(true);
        dispatch(userSetupSlice.actions.setExitMessageType(ExitType.UploadedSch1WithOtherGains));
        dispatch(push("/upload/sorry"));
      } else {
        setInvalidSchedule1Exists(false);
      }
    } else {
      setInvalidSchedule1Exists(false);
    }
  }, [lastSch1]);

  const handleNext = () => {
    if (taxState.irsSchedule1Forms.length) {
      dispatch(taxSlice.actions.confirmAllSchedule1Request());
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const deleteFile = (index: number) => {
    updateEditMode(index)(false);
    dispatch(taxSlice.actions.deleteSchedule1Request(index));
  };

  const onDropAccepted = (acceptedFiles: any) => {
    dispatch(taxSlice.actions.uploadSchedule1Request(acceptedFiles[0] as File));
  };

  const [fileDropError, setFileDropError] = useState("");
  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    setFileDropError("Upload failed: " + rejectedFiles[0].errors[0].message);
    setTimeout(() => setFileDropError(""), 4000);
  };

  const updateEditMode = (index: number) => (value: boolean) => {
    dispatch(taxSlice.actions.toggleEditMode({ index, value }));
  };

  const totalFromFormData = (form_data: IRSSchedule1Form) =>
    Number(form_data.business_income_or_loss) +
    Number(form_data.cancellation_of_debt) +
    Number(form_data.farm_income_or_loss) +
    Number(form_data.gambling_income) +
    Number(form_data.jury_duty_pay) +
    Number(form_data.other_gains_or_losses) +
    Number(form_data.other_income) +
    Number(form_data.rentals_royalties_etc) +
    Number(form_data.scholarship_and_grant_income);

  /*  
  const totalSch1 = taxState.irsSchedule1Forms
    .filter((sch1) => sch1.document_name !== "error")
    .map((sch1) => (sch1 as TaxDocument<IRSSchedule1Form>).form_data)
    .map(totalFromFormData)
    .reduce((acc, total) => acc + total, 0);

  const otherIncome1040 = Number(
    (taxState.irs1040 as TaxDocument<IRS1040Form>)?.form_data?.other_income ?? 0
  );

  const incomeMatches =
    Number(otherIncome1040) + 700 > totalSch1 &&
    Number(otherIncome1040) - 700 < totalSch1;*/

  const isNextDisabled = () => {
    if (!taxState.irsSchedule1Forms.length) {
      return !taxState.uploadedSchedule1s?.length;
    }
    return (
      invalidSchedule1Exists ||
      Object.values(taxState.editMode).includes(true) ||
      taxState.irsSchedule1Forms.some((form) => form.document_name === "error")
    );
  };

  return (
    <>
      <CustomStepper
        steps={steps}
        activeStep={taxState.irsSchedule1Forms.length ? 1 : 0}
        handleNext={handleNext}
        handleBack={() => dispatch(goBack())}
        className="upload-stepper"
        showBackButton={true}
        disableSteps={true}
        nextBtnDisable={isNextDisabled()}
      >
        {!taxState.uploadedSchedule1s ? (
          <LineLoader />
        ) : (
          <FormLayout
            title={"Schedule 1 Form"}
            subtitle={"File upload"}
            description={<span>{content.description}</span>}
            className="upload-form-layout"
            icon={
              <ContactSupportIcon
                titleAccess="Help"
                className={"help-icon"}
                onClick={handleOpen}
              />
            }
          >
            <h5>Documents ({taxState.irsSchedule1Forms.length})</h5>
            <div className="upload-column-wrapper">
              <DocumentList
                documents={taxState.irsSchedule1Forms}
                formName="Schedule 1"
                title={(document) =>
                  document.uploaded_file.file_name ?? "Schedule 1 Form"
                }
                onDropAccepted={onDropAccepted}
                properties={[
                  {
                    name: "Total gains or losses",
                    prop: (form) => totalFromFormData(form as IRSSchedule1Form),
                  },
                ]}
                showUpload={
                  /*!incomeMatches &&*/
                  taxState.irsSchedule1Forms.length < 1 &&
                  !invalidSchedule1Exists
                }
              />
              <div className="activeDocument">
                {taxState.selectedFormIndex === -1 ? (
                  <FileDropContainer
                    uploadTask={uploadSch1Task}
                    onDropAccepted={onDropAccepted}
                    onDropRejected={onDropRejected}
                  />
                ) : (
                  <ConfirmSchedule1
                    index={taxState.selectedFormIndex}
                    editMode={true}
                    toggleEditMode={updateEditMode(taxState.selectedFormIndex)}
                    deleteFile={deleteFile}
                    sch1={
                      taxState.irsSchedule1Forms[
                        taxState.selectedFormIndex
                      ] as TaxDocument<IRSSchedule1Form>
                    }
                  />
                )}
                <div className="mobileDocumentList">
                  {(taxState.irsSchedule1Forms.length < 1 && !invalidSchedule1Exists) && (
                    <FileDropContainer
                      uploadTask={uploadSch1Task}
                      onDropAccepted={onDropAccepted}
                      onDropRejected={onDropRejected}
                    />
                  )}
                  {taxState.irsSchedule1Forms.map(
                    (
                      fileInfo:
                        | TaxDocument<IRSSchedule1Form>
                        | TaxDocumentError,
                      index: number
                    ) =>
                      fileInfo.document_name === "error" ? (
                        <ErrorAlert>{fileInfo.message}</ErrorAlert>
                      ) : (
                        <ConfirmSchedule1
                          index={index}
                          editMode={true}
                          toggleEditMode={updateEditMode(
                            taxState.selectedFormIndex
                          )}
                          deleteFile={deleteFile}
                          sch1={fileInfo}
                        />
                      )
                  )}
                </div>
              </div>
            </div>

            {
              /* This is really only here so if user hits back button the only interaction they
            can have with the page is deleting the invalid Schedule 1 */
              invalidSchedule1Exists && (
                <ErrorAlert>
                  {
                    "Schedule 1 detected with income that would necessitate uploading a Form 4797 and/or Schedule E. Please delete if uploaded in error, otherwise you will be redirected."
                  }
                </ErrorAlert>
              )
            }
            {lastSch1 && !uploadSch1Task && (
              <Alert
                severity={
                  lastSch1.document_name === "error" ? "error" : "success"
                }
                variant="outlined"
                classes={{
                  root:
                    lastSch1.document_name === "error"
                      ? "error-alert floating-alert"
                      : "success-alert floating-alert",
                  message: "success-msg",
                  icon:
                    lastSch1.document_name === "error"
                      ? "error-icon"
                      : "success-icon",
                }}
                iconMapping={{
                  success: <CheckCircleIcon />,
                  error: <InfoCircleIcon />,
                }}
              >
                {!(lastSch1.document_name === "error")
                  ? "Your upload was successful."
                  : "Your upload failed, please try again."}
              </Alert>
            )}
            {fileDropError && (
              <ErrorAlert floating>{fileDropError}</ErrorAlert>
            )}
            {taxState.tax_return?.IRSSchedule1.messages?.map((message) => (
              <ErrorAlert>{message}</ErrorAlert>
            ))}

            {taxState.irsSchedule1Forms.length > 0 && <Paper elevation={0} className="infoContainer w-match">
            If the amounts you see here don’t match your Federal Schedule 1, make corrections before selecting
“continue.”
            </Paper>}
          </FormLayout>
        )}
      </CustomStepper>

      <AboutHelpModal
        open={open}
        formType={FormTypes.schedule1}
        handleClose={handleClose}
      />
      {(uploadSch1Task || confirmSch1Task || deleteSch1Task) && <LineLoader />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  taxState: state.tax,
  user: state.user.data,
});

export default connect(mapStateToProps)(FormsSchedule1);

const content: any = {
  title: "Schedule 1 Form",
  description: `It looks like you have additional income reported on your federal return,\nsome of which may be taxable to the city. Please upload your Federal\nSchedule 1 so we can help you determine if any of this income is taxable.`,
  confirmDescription: `If the amounts you see here don't match what was on your Federal return,\nuse the edit tool to make corrections.`,
};
